/**
 * Mock stream used until slices 10+11 land the real /chat endpoint.
 * Emits one of each event type the widget is expected to render, timed
 * to mimic a streaming LLM turn so UI state machines get exercised.
 *
 * Enable with `data-showpilot-mock="true"` or
 * `window.SHOWPILOT_CONFIG.mock = true`. Drop this file once slice 10
 * ships — the build stays under 25 KB gzip either way.
 */

import type { ChatEvent } from '@showpilot/core';

export interface MockStreamOptions {
  sessionId: string;
  message: string;
  onEvent: (event: ChatEvent) => void;
  signal?: AbortSignal;
}

export async function mockStream(opts: MockStreamOptions): Promise<void> {
  const chunks = splitForStream(mockReplyFor(opts.message));
  const start = Date.now();

  for (const chunk of chunks) {
    if (opts.signal?.aborted) throw new Error('aborted');
    await delay(70);
    opts.onEvent({
      type: 'message',
      payload: { text: chunk, partial: true },
    });
  }

  opts.onEvent({
    type: 'suggestions',
    payload: {
      items: [{ label: 'Tell me more' }, { label: 'How do I get started?' }],
    },
  });

  opts.onEvent({
    type: 'done',
    payload: {
      sessionId: opts.sessionId,
      tokensUsed: chunks.join('').split(/\s+/).length * 4,
      durationMs: Date.now() - start,
    },
  });
}

function mockReplyFor(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "I'm here when you're ready.";
  return `Got it — you said "${trimmed}". This is a mock response from the widget scaffold; the real chat API lands in slice 10.`;
}

function splitForStream(text: string): string[] {
  // Split on whitespace boundaries but preserve the spaces.
  const out: string[] = [];
  const re = /\S+\s*/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) out.push(m[0]);
  return out;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
